import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatKWD } from "@/lib/format";

function Stat({ label, value, hint, testid }) {
  return (
    <div className="bg-white border border-border p-5" data-testid={testid}>
      <div className="text-xs text-text-muted mb-2">{label}</div>
      <div className="text-2xl font-medium">{value}</div>
      {hint && <div className="text-[11px] text-text-muted mt-1">{hint}</div>}
    </div>
  );
}
const PAY = { cash: "نقداً", knet: "كي نت" };

function todayISO(offsetDays = 0) {
  // Kuwait local date (UTC+3)
  const d = new Date(Date.now() + 3 * 3600 * 1000 - offsetDays * 86400000);
  return d.toISOString().slice(0, 10);
}

export default function AdminReports() {
  const [range, setRange] = useState({ from: todayISO(29), to: todayISO(0) });
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [closings, setClosings] = useState([]);
  const [reportsEmail, setReportsEmail] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const reqRef = useRef(0);

  const load = (r = range) => {
    const token = ++reqRef.current;
    setLoading(true); setError("");
    const p = {}; if (r.from) p.from = r.from; if (r.to) p.to = r.to;
    api.get("/admin/reports", { params: p })
      .then((res) => { if (token === reqRef.current) { setD(res.data); setLoading(false); } })
      .catch((e) => { if (token === reqRef.current) { setError(formatApiError(e.response?.data?.detail) || "تعذّر تحميل بيانات الفترة"); setLoading(false); } });
  };

  useEffect(() => {
    load();
    api.get("/admin/closings").then((r) => setClosings(r.data)).catch(() => {});
    api.get("/settings").then((r) => { setReportsEmail(r.data.reports_email || r.data.email || ""); }).catch(() => {});
  }, []);

  const quick = (kind) => {
    let r = range;
    if (kind === "today") r = { from: todayISO(0), to: todayISO(0) };
    else if (kind === "week") r = { from: todayISO(6), to: todayISO(0) };
    else if (kind === "month") r = { from: todayISO(29), to: todayISO(0) };
    setRange(r); load(r);
  };

  const saveEmail = async () => {
    const v = emailDraft.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error("بريد إلكتروني غير صالح"); return; }
    try {
      const { data } = await api.put("/admin/settings", { reports_email: v });
      setReportsEmail(data.reports_email || v); setEditingEmail(false);
      toast.success("تم تحديث بريد التقارير المعتمد");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendReport = async () => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/report-email", { date_from: range.from, date_to: range.to });
      if (data.status === "sent") toast.success(`تم إرسال تقرير الفترة (${data.period}) إلى ${data.recipient}`);
      else toast.error(`تعذّر الإرسال: ${data.error || data.status}`);
      setConfirming(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const exportCsv = async () => {
    const p = {}; if (range.from) p.from = range.from; if (range.to) p.to = range.to;
    try {
      const res = await api.get("/admin/report-export", { params: p, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `rnf-report-${range.from}_${range.to}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("تعذّر التصدير"); }
  };

  return (
    <div className="p-5 md:p-8">
      <h1 className="font-display text-3xl mb-2">التقارير والمحاسبة</h1>
      <p className="text-sm text-text-muted mb-5">جميع الأرقام محسوبة من الفواتير المدفوعة بتوقيت الكويت (يُحتسب يوم النهاية كاملاً). المبالغ بالدينار الكويتي (٣ منازل).</p>

      <div className="flex flex-wrap items-end gap-3 mb-2">
        <button onClick={() => quick("today")} data-testid="report-today" className="border border-border px-4 py-2 text-sm">اليوم</button>
        <button onClick={() => quick("week")} data-testid="report-week" className="border border-border px-4 py-2 text-sm">الأسبوع</button>
        <button onClick={() => quick("month")} data-testid="report-month" className="border border-border px-4 py-2 text-sm">الشهر</button>
        <div><label className="text-xs text-text-muted block mb-1">من</label><input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="report-from" className="border border-border px-3 py-2 text-sm" /></div>
        <div><label className="text-xs text-text-muted block mb-1">إلى</label><input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="report-to" className="border border-border px-3 py-2 text-sm" /></div>
        <button onClick={() => load()} data-testid="report-apply" className="bg-primary text-primary-foreground px-6 py-2 text-sm">تطبيق</button>
        <button onClick={exportCsv} data-testid="report-export-csv" className="border border-primary text-primary px-6 py-2 text-sm">تصدير CSV</button>
      </div>
      <p className="text-xs text-accent mb-6" data-testid="report-active-range">الفترة المعروضة: {range.from || "—"} إلى {range.to || "—"}</p>

      {/* Send report to owner email */}
      <div className="bg-white border border-border p-5 mb-6" data-testid="report-email-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <div className="text-text-muted text-xs mb-1">بريد التقارير المعتمد</div>
            {!editingEmail ? (
              <div className="flex items-center gap-2">
                <span dir="ltr" data-testid="reports-email-value">{reportsEmail || "—"}</span>
                <button onClick={() => { setEmailDraft(reportsEmail); setEditingEmail(true); }} data-testid="reports-email-edit" className="text-accent underline text-xs">تعديل</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} dir="ltr" data-testid="reports-email-input" className="border border-border px-3 py-1.5 text-sm" />
                <button onClick={saveEmail} data-testid="reports-email-save" className="bg-[#141110] text-white px-3 py-1.5 text-xs">حفظ</button>
                <button onClick={() => setEditingEmail(false)} className="border border-border px-3 py-1.5 text-xs">إلغاء</button>
              </div>
            )}
          </div>
          <button onClick={() => setConfirming(true)} disabled={editingEmail} data-testid="report-send-email"
            className="flex items-center gap-2 bg-[#141110] text-white px-5 py-2.5 text-sm disabled:opacity-50">
            <Mail className="w-4 h-4" /> إرسال تقرير الفترة المحددة إلى إيميلي
          </button>
        </div>

        {confirming && (
          <div className="mt-4 border-t border-border pt-4" data-testid="report-send-confirm">
            <p className="text-sm mb-1">سيتم إرسال تقرير الفترة <b>{range.from} إلى {range.to}</b></p>
            <p className="text-sm mb-3">إلى البريد: <b dir="ltr">{reportsEmail}</b></p>
            <div className="flex gap-2">
              <button onClick={sendReport} disabled={sending} data-testid="report-send-confirm-btn" className="flex items-center gap-2 bg-[#141110] text-white px-5 py-2 text-sm disabled:opacity-60">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />} تأكيد الإرسال
              </button>
              <button onClick={() => setConfirming(false)} disabled={sending} className="border border-border px-5 py-2 text-sm">إلغاء</button>
            </div>
          </div>
        )}
      </div>

      {loading && <div className="flex items-center gap-2 text-text-muted py-8" data-testid="report-loading"><Loader2 className="w-4 h-4 animate-spin" /> جارٍ تحميل بيانات الفترة…</div>}
      {error && !loading && (
        <div className="flex items-center gap-2 text-destructive bg-destructive/5 border border-destructive/30 p-4 mb-6" data-testid="report-error">
          <AlertCircle className="w-4 h-4" /> {error}
          <button onClick={() => load()} className="underline text-sm ms-2">إعادة المحاولة</button>
        </div>
      )}

      {!loading && !error && d && !d.has_data && (
        <div className="bg-white border border-border p-8 text-center text-text-muted" data-testid="report-empty">لا توجد عمليات ضمن الفترة المختارة ({range.from} إلى {range.to}).</div>
      )}

      {!loading && !error && d && d.has_data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat label="إجمالي المدفوع (شامل التوصيل)" value={formatKWD(d.paid_total)} hint={`${d.invoice_count} فاتورة · ${d.units_sold} قطعة`} testid="report-sales" />
            <Stat label="صافي المبيعات (بعد المستردات)" value={formatKWD(d.net_sales)} hint={`المستردات ${formatKWD(d.refund_total)}`} testid="acc-net" />
            <Stat label="مبيعات المحل" value={formatKWD(d.store_sales)} hint={`${d.store_invoices} فاتورة محل`} testid="acc-store" />
            <Stat label="مبيعات الموقع" value={formatKWD(d.web_sales)} hint={`${d.web_invoices} فاتورة موقع`} testid="report-web" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat label="رسوم التوصيل المحلي" value={formatKWD(d.local_delivery_fees)} testid="acc-local-fee" />
            <Stat label="رسوم الشحن الدولي" value={formatKWD(d.intl_shipping_fees)} testid="acc-intl-fee" />
            <Stat label="بانتظار الدفع (غير محصّل)" value={formatKWD(d.awaiting_payment)} hint={`${d.awaiting_count} طلب — لا يُحتسب ضمن المدفوع`} testid="acc-awaiting" />
            <Stat label="الفواتير الملغاة" value={`${d.cancelled_count} (${formatKWD(d.cancelled_value)})`} hint="مستبعدة من المبيعات" testid="acc-cancelled" />
          </div>
          <div className="bg-white border border-border p-5 mb-6">
            <h2 className="text-sm tracking-luxe uppercase text-accent mb-4">المبيعات المدفوعة حسب طريقة الدفع</h2>
            {(() => {
              const bm = d.by_method || {};
              const legacy = Object.entries(bm).filter(([m, v]) => m !== "cash" && m !== "knet" && v > 0).reduce((s, [, v]) => s + v, 0);
              return (
                <>
                  {["cash", "knet"].map((m) => <div key={m} data-testid={`by-method-${m}`} className="flex justify-between text-sm border-b border-border pb-2 mb-2"><span>{PAY[m]}</span><span>{formatKWD(bm[m] || 0)}</span></div>)}
                  {legacy > 0 && <div data-testid="by-method-legacy" className="flex justify-between text-xs text-text-muted pb-2"><span>طرق دفع سابقة (معاملات تاريخية)</span><span>{formatKWD(legacy)}</span></div>}
                </>
              );
            })()}
          </div>
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white border border-border p-5">
              <h2 className="text-sm tracking-luxe uppercase text-accent mb-4">أداء البائعات ومبيعات الموقع</h2>
              {Object.entries(d.seller_performance).length === 0 ? <p className="text-text-muted text-sm">لا يوجد</p> :
                Object.entries(d.seller_performance).map(([s, v]) => <div key={s} className="flex justify-between text-sm border-b border-border pb-2 mb-2"><span>{s} ({v.count})</span><span>{formatKWD(v.value)}</span></div>)}
            </div>
            <div className="bg-white border border-border p-5">
              <h2 className="text-sm tracking-luxe uppercase text-accent mb-4">الإقفالات اليومية للبائعات</h2>
              {closings.length === 0 ? <p className="text-text-muted text-sm">لا يوجد</p> :
                closings.map((c) => <div key={c.id} className="flex justify-between text-sm border-b border-border pb-2 mb-2" data-testid={`closing-${c.id}`}><span>{c.seller_name} · {c.day}</span><span className={Math.abs(c.difference) >= 0.001 ? "text-amber-600" : ""}>فرق {formatKWD(c.difference)}</span></div>)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
